/*******************************************************
* ForgotPasswordDialog
* 
* Dialog component for handling password reset workflow.
* Manages the display of the password reset form and success messages.
* 
* Key Features:
* - Displays password reset form in a modal dialog
* - Handles success/error states
* - Maintains consistent styling with other dialogs
********************************************************/
'use client';
//########## IMPORTS ##########
// #region IMPORTS
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Box,
    Button,
    Typography,
    Slide,
} from '@mui/material';
import { type TransitionProps } from '@mui/material/transitions';
import Image from 'next/image';
import swishLogo from '@/public/assets/media/Logos/swish.png';
import FormForgotPassword from './FormForgotPassword';
// #endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
interface ForgotPasswordDialogProps {
    open: boolean;
    onClose: () => void;
    setMainDialogOpen: (open: boolean) => void;
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const dialogStyles = {
    '& .MuiDialog-container': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    '& .MuiDialog-paper': {
        m: 2,
        maxWidth: 'phoneBig',
        width: '100%'
    }
}
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS

//#endregion


export default function ({ open, onClose, setMainDialogOpen }: ForgotPasswordDialogProps) {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC

    const [successDialog, setSuccessDialog] = useState({
        open: false,
        message: ''
    });

    const handleClose = () => {
        setMainDialogOpen(false);
        onClose();
    };

    const handleSuccessDialogClose = () => {
        setSuccessDialog({ open: false, message: '' });
        handleClose();
    };

    const handleSuccess = (message: string) => {
        setMainDialogOpen(false);
        setSuccessDialog({
            open: true,
            message
        });
    };
    //#endregion

    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <>
            <Dialog
                open={open}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick') {
                        return;
                    }
                    handleClose();
                }}
                sx={dialogStyles}
                fullWidth
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
                    {/* Logo and Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
                        <Box sx={{ width: 40, height: 40, position: 'relative', mr: 2 }}>
                            <Image
                                src={swishLogo}
                                alt="Swish Logo"
                                fill
                                style={{ objectFit: 'contain' }}
                                priority
                            />
                        </Box>
                        <Typography variant="h6" noWrap component="div">
                            Reset Password
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <FormForgotPassword
                        onSuccess={handleSuccess}
                        onCancel={handleClose}
                    />
                </DialogContent>
            </Dialog>

            {/* Success Dialog */}
            <Dialog
                open={successDialog.open}
                slots={{
                    transition: Transition
                }}
                keepMounted
                onClose={handleSuccessDialogClose}
                aria-describedby="alert-dialog-success-description"
                sx={dialogStyles}
            >
                <DialogTitle>{"Password Reset Complete"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-success-description">
                        {successDialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSuccessDialogClose} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
    //#endregion
}
